import { handleError, ImpersonationService, UserStateService } from '@/app/core';
import { environment } from '@/environments/environment';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { catchError, map, tap } from 'rxjs';

interface FlatMenuItem {
    id: number;
    label: string;
    ruta?: string | null;
    icono?: string | null;
    parentId?: number | null;
    orden?: number;
    isHeading?: boolean;
    children?: FlatMenuItem[];
}

@Injectable({
    providedIn: 'root'
})
export class AppMenuService {
    private readonly userState = inject(UserStateService);
    private readonly impersonation = inject(ImpersonationService);
    private apiUrl = `${environment.api}menu`;
    private readonly http = inject(HttpClient);
    private readonly cassAdminRoleSlugs = new Set(['gg_cass_admin', 'gg_admin_cass']);
    private readonly cassAdminPermissionCodes = new Set([
        'gg_admin_cass',
        'gg_cass_stats_ver_todos',
        'gg_cass_parametros',
        'gg_cass_param_admin',
        'gg_cass_param_gestion_empresa',
        'gg_cass_admin_usuarios_crear'
    ]);
    private readonly calidadAdminRoleSlugs = new Set(['gg_calidad_admin', 'gg_admin_calidad']);
    private readonly calidadNotificationPermissionCodes = new Set([
        'gg_calidad_checklist_plantilla_crear',
        'gg_calidad_checklist_plantilla_editar'
    ]);

    getMenu() {
        const sitemaId = this.userState.getPermisos();
        return this.http.get<MenuItem[]>(this.apiUrl).pipe(
            catchError((error) => handleError(error)),
            map((response) => response),
        );
    }

    getMenuItems(): MenuItem[] {
        const flatMenu = this.userState.getMenu();
        const mapped = flatMenu.map((item) => this.mapToMenuItem(item));

        if (this.shouldEnableCassImpersonationMenu()) {
            mapped.push({
                label: 'Vista Usuario GRS',
                icon: 'pi pi-eye',
                routerLink: ['/cass/admin/ver-usuario'],
            });
        }

        this.ensureCalidadImpersonationMenu(mapped);
        this.ensureCalidadNotificationsMenu(mapped);

        return mapped;
    }

    private ensureCalidadImpersonationMenu(items: MenuItem[]): void {
        if (!this.shouldEnableCalidadImpersonationMenu()) return;

        const route = '/calidad/admin/ver-usuario';
        if (this.hasRoute(items, route)) return;

        const vistaUsuarioItem: MenuItem = {
            label: 'Vista Usuario Calidad',
            icon: 'pi pi-eye',
            routerLink: [route],
        };

        const calidadRoot = items.find((item) => this.normalizeLabel(item.label ?? '') === 'calidad');
        if (calidadRoot?.items) {
            calidadRoot.items.push(vistaUsuarioItem);
            return;
        }

        items.push(vistaUsuarioItem);
    }

    private ensureCalidadNotificationsMenu(items: MenuItem[]): void {
        if (this.userState.getSistemaActivo()?.id !== 4) return;

        const permisos = this.extractPermissionCodes(this.userState.getPermisos());
        if (!permisos.some((code) => this.calidadNotificationPermissionCodes.has(code))) return;

        const route = '/calidad/notification-groups';
        if (this.hasRoute(items, route)) return;

        const notificationItem: MenuItem = {
            label: 'Notificaciones',
            icon: 'pi pi-bell',
            routerLink: [route]
        };
        const calidadRoot = items.find((item) => this.normalizeLabel(item.label ?? '') === 'calidad');
        if (calidadRoot?.items) {
            calidadRoot.items.push(notificationItem);
            return;
        }

        items.push(notificationItem);
    }

    private hasRoute(items: MenuItem[], route: string): boolean {
        return items.some((item) => {
            const routerLink = Array.isArray(item.routerLink) ? item.routerLink[0] : item.routerLink;
            return routerLink === route || this.hasRoute(item.items ?? [], route);
        });
    }

    private shouldEnableCassImpersonationMenu(): boolean {
        const user = this.userState.currentUser as any;
        const context = this.userState.currentContext as any;
        const gruposUser = Array.isArray(user?.grupos) ? user.grupos : [];
        const gruposContext = Array.isArray(context?.grupos) ? context.grupos : [];
        const grupos = [...gruposUser, ...gruposContext];

        const permisos = this.extractPermissionCodes(this.userState.getPermisos() ?? context?.permisos ?? []);
        const grupoSlugs = this.extractGroupSlugs(grupos);

        const hasRole = grupoSlugs.some((slug) => this.cassAdminRoleSlugs.has(slug));
        const hasPermiso = permisos.some((code) => this.cassAdminPermissionCodes.has(code));

        return hasRole || hasPermiso;
    }

    private shouldEnableCalidadImpersonationMenu(): boolean {
        if (this.impersonation.isImpersonating()) return false;
        if (this.userState.getSistemaActivo()?.id !== 4) return false;

        const context = this.userState.currentContext as any;
        const permisos = this.extractPermissionCodes(this.userState.getPermisos() ?? context?.permisos ?? []);
        // El contexto agrega el nombre del grupo como permiso operativo (gg_calidad_admin).
        if (permisos.some((code) => this.calidadAdminRoleSlugs.has(code) || code === 'gg_calidad_vista_usuario')) {
            return true;
        }

        const user = this.userState.currentUser as any;
        const gruposUser = Array.isArray(user?.grupos) ? user.grupos : [];
        const gruposContext = Array.isArray(context?.grupos) ? context.grupos : [];

        // context.grupos suele ser number[] (grupoIds); user.grupos puede traer objetos.
        if (gruposContext.some((g: unknown) => Number(g) === 6)) return true;

        return gruposUser.some((g: any) => {
            const sistemaId = Number(g?.sistemaId ?? g?.sistema?.id);
            const grupoId = Number(g?.grupoId ?? g?.id ?? g?.grupo?.id);
            return sistemaId === 4 && grupoId === 6;
        });
    }

    private extractGroupSlugs(source: unknown): string[] {
        if (!Array.isArray(source)) return [];
        const slugs = source
            .map((item: any) => item?.nombre)
            .map((value) => (typeof value === 'string' ? value.trim().toLowerCase() : ''))
            .filter((value) => value.length > 0);
        return Array.from(new Set(slugs));
    }

    private extractPermissionCodes(source: unknown): string[] {
        if (!Array.isArray(source)) return [];
        const codes = source
            .map((item: any) => {
                if (typeof item === 'string') return item;
                return item?.code ?? item?.codigo ?? item?.permiso ?? item?.nombre ?? '';
            })
            .map((value) => String(value).trim().toLowerCase())
            .filter((value) => value.length > 0);
        return Array.from(new Set(codes));
    }

    private readonly ROUTE_ICON_MAP: Record<string, string> = {
        // CASS — Inspecciones (Checklist)
        'cass/dashboard':                           'chart-bar',
        'cass/dashboard-cass':                      'chart-bar',
        'cass/checklist/dashboard':                 'check-square',
        'cass/checklist/nueva-inspeccion':          'file-plus',
        'cass/checklist/historico':                 'history',
        'cass/checklist/plantillas':                'copy',
        'cass/checklist/addPlanilla':               'pencil',
        // CASS — Tarjetas M.A.S
        'cass/tarjeta/dashboard':                   'table',
        'cass/nueva-tarjeta':                       'plus-circle',
        'cass/historico':                           'history',
        'cass/tarjeta/gestion-empresa':             'building',
        // CASS — Estadísticas
        'cass/estadisticas/dashboard':              'chart-pie',
        'cass/estadisticas/carga-estadisticas':     'chart-line',
        'cass/estadisticas/carga':                  'chart-line',
        'cass/estadisticas/historial':              'history',
        'cass/estadisticas/gestion-contratos':      'file-edit',
        'cass/estadisticas/certificados':           'verified',
        'cass/estadisticas/gestion-proactivos':     'shield',
        'cass/estadisticas/gestion-vehicular':      'car',
        'cass/estadisticas/solicitudes-carga':      'send',
        'cass/estadisticas/configuracion-carga':    'sliders-h',
        // Mantenimiento
        'mantenimiento/dashboard':                  'home',
        'mantenimiento/avisos':                     'bell',
        'mantenimiento/checklist/asignados':        'check-square',
        'mantenimiento/checklist/historico':        'history',
        'mantenimiento/desvios/pendientes':         'exclamation-triangle',
        'mantenimiento/mis-pendientes':             'inbox',
        'mantenimiento/ordenes_preventivas':        'wrench',
        'mantenimiento/historial-checklist':        'history',
        'mantenimiento/configuracion/semaforos':    'circle',
        'mantenimiento/plantillas':                 'copy',
        'mantenimiento/contratos':                  'file-edit',
        'mantenimiento/equipos':                    'cog',
        'mantenimiento/admin/usuarios':             'users',
        // Administración
        'administracion/dashboard':                 'home',
        'administracion/lista-usuarios':            'users',
        'administracion/agregar-usuario':           'user-plus',
        'administracion/carga-usuarios':            'upload',
        'administracion/gestion-empresa':           'building',
        'administracion/menu':                      'list',
        'administracion/menu-lista':                'list',
        'administracion/mantenimiento-sistema':     'cog',
        // Calidad
        'calidad/dashboard':                        'home',
        'calidad/dashboard-inspecciones':           'chart-bar',
        'calidad/checklist':                        'check-square',
        'calidad/historico-inspecciones':           'history',
        'calidad/plantillas':                       'copy',
        'calidad/nueva-plantilla':                  'file-plus',
        'calidad/plantillas/editor':                'pencil',
        'calidad/notification-groups':              'bell',
        'calidad/admin/ver-usuario':                'eye',
    };

    // Íconos genéricos del sistema anterior que deben ignorarse y reemplazarse
    private readonly GENERIC_ICONS = new Set(['circle', 'circle-fill', 'dot', 'bullet', 'minus', 'square']);

    private normalizeLabel(label: string): string {
        return label.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    }

    private resolveIconByKeyword(label: string): string | undefined {
        const l = this.normalizeLabel(label);
        const has = (...words: string[]) => words.every(w => l.includes(w));

        // Grupos / headings
        if (has('administracion'))                                      return 'cog';
        if (has('parametro') || has('configuracion'))                   return 'sliders-h';
        if (has('notificacion'))                                        return 'bell';
        if (l === 'estadisticas' || l === 'estadística' || l === 'estadistica') return 'chart-line';

        // Inspecciones / Checklist
        if (has('dashboard') && has('inspeccion'))                      return 'check-square';
        if ((has('nueva') || has('nuevo')) && has('inspeccion'))        return 'file-plus';
        if ((has('historico') || has('historial')) && has('inspeccion')) return 'history';
        if (has('plantilla'))                                           return 'copy';

        // Tarjetas M.A.S
        if (has('dashboard') && has('tarjeta'))                         return 'table';
        if ((has('nueva') || has('nuevo')) && has('tarjeta'))           return 'plus-circle';
        if ((has('historico') || has('historial')) && has('tarjeta'))   return 'history';
        if (has('gestion') && has('empresa'))                           return 'building';

        // Estadísticas
        if (has('dashboard') && has('estadistica'))                     return 'chart-pie';
        if (has('carga') && has('estadistica'))                         return 'chart-line';
        if ((has('historico') || has('historial')) && has('estadistica')) return 'history';
        if (has('gestion') && has('contrato'))                          return 'file-edit';
        if (has('certificado'))                                         return 'verified';
        if (has('gestion') && has('proactivo'))                         return 'shield';
        if (has('gestion') && has('vehicular'))                         return 'car';
        if (has('solicitud') && has('carga'))                           return 'send';
        if (has('configuracion') && has('carga'))                       return 'sliders-h';

        // Mantenimiento
        if (has('aviso'))                                               return 'bell';
        if (has('desvio'))                                              return 'exclamation-triangle';
        if (has('procesamiento'))                                       return 'play-circle';
        if (has('orden') && has('preventiva'))                          return 'wrench';
        if (has('contrato'))                                            return 'file-edit';
        if (has('equipo'))                                              return 'cog';

        // Usuarios / Empresas / Acceso
        if ((has('agregar') || has('nuevo') || has('nueva')) && has('usuario')) return 'user-plus';
        if (has('carga') && (has('usuario') || has('masiva')))          return 'upload';
        if (has('usuario'))                                             return 'users';
        if (has('empresa'))                                             return 'building';

        // Genéricos de último recurso
        if (has('dashboard'))                                           return 'chart-bar';
        if (has('historico') || has('historial'))                       return 'history';
        if (has('menu'))                                                return 'list';
        if (has('mantenimiento'))                                       return 'cog';

        return undefined;
    }

    private resolveIcon(icono?: string | null, ruta?: string | null, label?: string): string | undefined {
        if (icono && !this.GENERIC_ICONS.has(icono)) return `pi pi-${icono}`;
        if (ruta) {
            const key = ruta.replace(/^\//, '');
            const byRoute = this.ROUTE_ICON_MAP[key];
            if (byRoute) return `pi pi-${byRoute}`;
        }
        if (label) {
            const byKeyword = this.resolveIconByKeyword(label);
            if (byKeyword) return `pi pi-${byKeyword}`;
        }
        return undefined;
    }

    private mapToMenuItem(item: FlatMenuItem): MenuItem {
        const ruta = item.ruta
            ? (item.ruta.startsWith('/') ? item.ruta : '/' + item.ruta)
            : undefined;

        const menuItem: MenuItem = {
            label: item.label,
            icon: this.resolveIcon(item.icono, item.ruta, item.label),
            routerLink: ruta ? [ruta] : undefined,
        };

        if (item.children && item.children.length > 0) {
            menuItem.items = item.children.map((child) => this.mapToMenuItem(child));
        }

        return menuItem;
    }

    getPermisos() {
        return this.userState.getPermisos();
    }
}
